import { useRef, useState } from 'react'
import { dataApi } from '../api/client'
import { detectInputTypeFromFileName } from '../utils/formatMapping'
import { zipFolder, ZIP_WARN_BYTES } from '../utils/zipFolder'

// mago-3d-tiler --help 기준 -it 로 인정되는 값만 선택지로 제공 (AdvancedRegisterModal과 동일한 목록)
const INPUT_TYPES = [
  { val: 'auto', label: '자동 감지' },
  { val: 'obj', label: 'OBJ' },
  { val: '3ds', label: '3DS' },
  { val: 'fbx', label: 'FBX' },
  { val: 'gltf', label: 'glTF / GLB' },
  { val: 'las', label: 'LAS / LAZ' },
  { val: 'citygml', label: 'CityGML' },
  { val: 'shp', label: 'Shapefile' },
  { val: 'geojson', label: 'GeoJSON' },
  { val: 'gpkg', label: 'GeoPackage' },
  { val: 'kml', label: 'KML' },
]

const OUTPUT_TYPES = [
  { val: 'b3dm', label: 'B3DM (기본)' },
  { val: 'i3dm', label: 'I3DM' },
  { val: 'pnts', label: 'PNTS (Point Cloud)' },
]

const CRS_OPTIONS = [
  { code: '5186', label: 'EPSG:5186 — Korea 2000 Central' },
  { code: '5179', label: 'EPSG:5179 — Korea 2000 / UTM-K' },
  { code: '5174', label: 'EPSG:5174 — Korean 1985 Central' },
  { code: '5187', label: 'EPSG:5187 — Korea 2000 East' },
  { code: '32652', label: 'EPSG:32652 — UTM Zone 52N' },
  { code: '3857', label: 'EPSG:3857 — Web Mercator' },
  { code: '4326', label: 'EPSG:4326 — WGS 84' },
  { code: '4978', label: 'EPSG:4978 — WGS84 ECEF' },
]
const CUSTOM_CRS = 'custom'

const ACCEPTED_EXTS = ['.obj', '.ply', '.fbx', '.3ds', '.las', '.laz', '.gltf', '.glb', '.dae', '.ifc', '.gml', '.shp', '.geojson', '.gpkg', '.kml', '.zip']

interface SourceRow {
  file: File
  inputType: string
  crs: string
  customCrs: string
  proj: string
  xOffset: string
  yOffset: string
  zOffset: string
  longitude: string
  latitude: string
  status: 'pending' | 'uploading' | 'done' | 'error'
}

function newRow(file: File): SourceRow {
  return {
    file,
    inputType: detectInputTypeFromFileName(file.name),
    crs: '5186', customCrs: '', proj: '',
    xOffset: '0', yOffset: '0', zOffset: '0',
    longitude: '', latitude: '',
    status: 'pending',
  }
}

interface Props {
  onClose: () => void
  onSuccess: () => void
}

export default function MultiSourceMergeModal({ onClose, onSuccess }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const folderRef = useRef<HTMLInputElement | null>(null)

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [sources, setSources] = useState<SourceRow[]>([])
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null)

  const [outputType, setOutputType] = useState('b3dm')
  const [tilesVersion, setTilesVersion] = useState('1.1')
  const [quantize, setQuantize] = useState(false)
  const [recursive, setRecursive] = useState(false)
  const [minLod, setMinLod] = useState(0)
  const [maxLod, setMaxLod] = useState(3)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [zippingFolder, setZippingFolder] = useState(false)
  const [zipFolderPct, setZipFolderPct] = useState(0)

  const isBusy = submitting || zippingFolder
  const valid = name.trim().length > 0 && sources.length >= 2
    && sources.every(s => s.crs !== CUSTOM_CRS || /^\d+$/.test(s.customCrs.trim()))

  const reset = () => {
    setName(''); setDescription(''); setSources([]); setExpandedIndex(null)
    setOutputType('b3dm'); setTilesVersion('1.1'); setQuantize(false); setRecursive(false)
    setMinLod(0); setMaxLod(3); setSubmitting(false); setError('')
    setZippingFolder(false); setZipFolderPct(0)
  }
  const handleClose = () => { if (!isBusy) { reset(); onClose() } }

  const addFiles = (files: FileList) => {
    const accepted = Array.from(files).filter(f => ACCEPTED_EXTS.some(ext => f.name.toLowerCase().endsWith(ext)))
    setSources(prev => [...prev, ...accepted.map(newRow)])
  }

  // 상세 옵션 등록의 폴더 업로드와 동일한 방식 — 폴더 전체를 압축해 "하나의 원본"으로 추가
  // (여러 파일로 나뉜 하나의 데이터셋, 예: OBJ+MTL+텍스처). 폴더 안 파일들을 개별 원본으로
  // 쪼개고 싶다면 "+ 파일 추가"로 직접 다중 선택하면 됨.
  const handlePickFolder = async (files: FileList) => {
    if (files.length === 0) return
    const arr = Array.from(files)
    const folderName = arr[0].webkitRelativePath.split('/')[0] || 'folder'
    const totalSize = arr.reduce((s, f) => s + f.size, 0)
    if (totalSize > ZIP_WARN_BYTES) {
      setError(`폴더 크기(${(totalSize / 1e9).toFixed(1)} GB)가 2 GB를 초과합니다. 미리 ZIP으로 압축한 뒤 파일 추가를 이용하면 더 안정적입니다.`)
    }
    setZippingFolder(true); setZipFolderPct(0)
    try {
      const blob = await zipFolder(arr, setZipFolderPct)
      const zipFile = new File([blob], `${folderName}.zip`, { type: 'application/zip' })
      setSources(prev => [...prev, newRow(zipFile)])
    } catch {
      setError('폴더 압축에 실패했습니다. 다시 시도해주세요.')
    } finally {
      setZippingFolder(false)
    }
  }

  const updateRow = (index: number, patch: Partial<SourceRow>) => {
    setSources(prev => prev.map((s, i) => i === index ? { ...s, ...patch } : s))
  }

  const removeRow = (index: number) => {
    setSources(prev => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (!valid) return
    setError('')
    setSubmitting(true)

    let batchId: number | null = null
    try {
      const batch = await dataApi.startMergeBatch({ name: name.trim(), description: description.trim() || undefined })
      batchId = batch.id

      for (let i = 0; i < sources.length; i++) {
        const s = sources[i]
        updateRow(i, { status: 'uploading' })
        const fd = new FormData()
        fd.append('file', s.file)
        fd.append('crs', s.crs === CUSTOM_CRS ? s.customCrs.trim() : s.crs)
        fd.append('xOffset', s.xOffset || '0')
        fd.append('yOffset', s.yOffset || '0')
        fd.append('zOffset', s.zOffset || '0')
        if (s.proj.trim()) fd.append('proj', s.proj.trim())
        if (s.longitude.trim()) fd.append('longitude', s.longitude.trim())
        if (s.latitude.trim()) fd.append('latitude', s.latitude.trim())
        fd.append('inputType', s.inputType)
        await dataApi.addMergeBatchSource(batchId, fd)
        updateRow(i, { status: 'done' })
      }

      await dataApi.finalizeMergeBatch(batchId, { outputType, tilesVersion, quantize, recursive, minLod, maxLod })
      reset()
      onSuccess()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      setError(err.response?.data?.error ?? '병합 변환 시작에 실패했습니다.')
      setSubmitting(false)
      if (batchId != null) {
        dataApi.cancelMergeBatch(batchId).catch(() => {})
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4 backdrop-blur-sm" onClick={handleClose}>
      <div className="bg-white rounded-xl shadow-2xl flex flex-col" style={{ width: 760, maxWidth: '95vw', maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}>

        <div className="flex items-center gap-3 px-5 py-4 rounded-t-xl shrink-0"
          style={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #2563eb 100%)' }}>
          <div className="flex-1 min-w-0">
            <h2 className="text-[15px] font-bold text-white">다중 파일 변환 후 병합</h2>
            <p className="text-[11px] text-white/75 font-mono">여러 원본을 순차 변환한 뒤 mago-3d-tiler -m 옵션으로 병합</p>
          </div>
          {!isBusy && (
            <button onClick={handleClose} className="bg-white/20 hover:bg-white/30 text-white rounded p-1.5 transition text-sm leading-none">✕</button>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2.5 rounded text-sm">{error}</div>}

          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">병합 결과 이름 <span className="text-red-500">*</span></label>
              <input value={name} onChange={e => setName(e.target.value)} placeholder="예: 서울 전체 병합"
                disabled={submitting} className="input-field"/>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-slate-600 mb-1">설명</label>
              <input value={description} onChange={e => setDescription(e.target.value)} placeholder="비워두면 자동 생성"
                disabled={submitting} className="input-field"/>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-semibold text-slate-600">원본 파일 ({sources.length}개, 2개 이상 필요)</label>
              <div className="flex items-center gap-3">
                <button onClick={() => !isBusy && fileRef.current?.click()} disabled={isBusy}
                  className="text-xs text-primary-600 font-semibold hover:underline disabled:opacity-50">
                  + 파일 추가
                </button>
                <button onClick={() => !isBusy && folderRef.current?.click()} disabled={isBusy}
                  className="text-xs text-primary-600 font-semibold hover:underline disabled:opacity-50">
                  + 폴더 추가
                </button>
              </div>
              <input ref={fileRef} type="file" multiple className="sr-only"
                onChange={e => { if (e.target.files) addFiles(e.target.files); e.target.value = '' }}/>
              <input
                ref={(el) => {
                  if (el) el.webkitdirectory = true
                  folderRef.current = el
                }}
                type="file" multiple className="sr-only"
                onChange={e => { if (e.target.files) handlePickFolder(e.target.files); e.target.value = '' }}/>
            </div>
            <p className="text-[10px] text-slate-400 -mt-1 mb-2">폴더 선택 시 상세 옵션 등록과 동일하게 폴더 전체를 압축해 하나의 원본으로 추가합니다(예: OBJ+MTL+텍스처).</p>

            {zippingFolder && (
              <div className="mb-2">
                <div className="flex justify-between text-[10px] text-slate-500 mb-1">
                  <span>폴더 압축 중...</span>
                  <span>{zipFolderPct}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-1.5">
                  <div className="bg-primary-400 h-1.5 rounded-full transition-all duration-100" style={{ width: `${zipFolderPct}%` }}/>
                </div>
              </div>
            )}

            {sources.length === 0 ? (
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center text-sm text-slate-400">
                병합할 원본 파일을 2개 이상 추가해주세요.
              </div>
            ) : (
              <ul className="space-y-2">
                {sources.map((s, i) => {
                  const crsIsCustom = s.crs === CUSTOM_CRS
                  const rowCrsValid = !crsIsCustom || /^\d+$/.test(s.customCrs.trim())
                  return (
                    <li key={i} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="flex items-center gap-2 px-3 py-2 bg-slate-50">
                        {s.status === 'uploading' && <span className="text-xs text-primary-600">⟳</span>}
                        {s.status === 'done' && <span className="text-xs text-green-600">✓</span>}
                        {s.status === 'error' && <span className="text-xs text-red-600">✕</span>}
                        <span className="text-sm font-medium text-slate-700 truncate flex-1">{s.file.name}</span>
                        <span className="text-[10px] text-slate-400">{(s.file.size / 1024 / 1024).toFixed(2)} MB</span>
                        <button onClick={() => setExpandedIndex(expandedIndex === i ? null : i)} disabled={submitting}
                          className="text-[11px] text-slate-500 hover:text-primary-600 disabled:opacity-50">
                          {expandedIndex === i ? '접기' : '좌표 설정'}
                        </button>
                        <button onClick={() => removeRow(i)} disabled={submitting}
                          className="text-slate-400 hover:text-red-500 disabled:opacity-50 text-sm leading-none">✕</button>
                      </div>
                      {expandedIndex === i && (
                        <div className="p-3 space-y-2 border-t border-slate-200">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[10px] font-semibold text-slate-500 mb-1">입력 포맷</label>
                              <select value={s.inputType} onChange={e => updateRow(i, { inputType: e.target.value })}
                                disabled={submitting} className="input-field text-xs">
                                {INPUT_TYPES.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                              </select>
                            </div>
                            <div>
                              <label className="block text-[10px] font-semibold text-slate-500 mb-1">CRS</label>
                              <select value={s.crs} onChange={e => updateRow(i, { crs: e.target.value })}
                                disabled={submitting} className="input-field text-xs">
                                {CRS_OPTIONS.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                                <option value={CUSTOM_CRS}>직접 입력</option>
                              </select>
                            </div>
                          </div>
                          {crsIsCustom && (
                            <div>
                              <input type="text" inputMode="numeric" value={s.customCrs}
                                onChange={e => updateRow(i, { customCrs: e.target.value.replace(/[^0-9]/g, '') })}
                                placeholder="EPSG 코드 (예: 5181)" disabled={submitting} className="input-field text-xs"/>
                              {s.customCrs.trim().length > 0 && !rowCrsValid && (
                                <p className="text-[10px] text-red-600 mt-1">숫자로 된 EPSG 코드를 입력해주세요.</p>
                              )}
                            </div>
                          )}
                          <div>
                            <label className="block text-[10px] font-semibold text-slate-500 mb-1">Proj4 (입력 시 CRS보다 우선)</label>
                            <input value={s.proj} onChange={e => updateRow(i, { proj: e.target.value })}
                              placeholder="+proj=tmerc ..." disabled={submitting} className="input-field text-xs font-mono"/>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="block text-[10px] font-semibold text-slate-500 mb-1">X Offset</label>
                              <input type="number" step="any" value={s.xOffset} onChange={e => updateRow(i, { xOffset: e.target.value })}
                                disabled={submitting} className="input-field text-xs"/>
                            </div>
                            <div>
                              <label className="block text-[10px] font-semibold text-slate-500 mb-1">Y Offset</label>
                              <input type="number" step="any" value={s.yOffset} onChange={e => updateRow(i, { yOffset: e.target.value })}
                                disabled={submitting} className="input-field text-xs"/>
                            </div>
                            <div>
                              <label className="block text-[10px] font-semibold text-slate-500 mb-1">Z Offset</label>
                              <input type="number" step="any" value={s.zOffset} onChange={e => updateRow(i, { zOffset: e.target.value })}
                                disabled={submitting} className="input-field text-xs"/>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-[10px] font-semibold text-slate-500 mb-1">경도 (로컬 데이터 원점)</label>
                              <input type="number" step="any" value={s.longitude} onChange={e => updateRow(i, { longitude: e.target.value })}
                                disabled={submitting} className="input-field text-xs"/>
                            </div>
                            <div>
                              <label className="block text-[10px] font-semibold text-slate-500 mb-1">위도</label>
                              <input type="number" step="any" value={s.latitude} onChange={e => updateRow(i, { latitude: e.target.value })}
                                disabled={submitting} className="input-field text-xs"/>
                            </div>
                          </div>
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            )}
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-slate-600 mb-2">공통 변환 옵션 (병합 결과 전체에 적용)</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">출력 포맷</label>
                <select value={outputType} onChange={e => setOutputType(e.target.value)} disabled={submitting} className="input-field text-xs">
                  {OUTPUT_TYPES.map(t => <option key={t.val} value={t.val}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">3D Tiles 버전</label>
                <div className="flex gap-2">
                  {['1.0', '1.1'].map(v => (
                    <button key={v} onClick={() => !submitting && setTilesVersion(v)}
                      className={[
                        'flex-1 py-1.5 rounded-md text-xs font-semibold border transition-colors',
                        tilesVersion === v ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-slate-200 bg-white text-slate-500',
                      ].join(' ')}>
                      v{v}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">최소 LOD</label>
                <input type="number" min={0} max={22} value={minLod} onChange={e => setMinLod(Number(e.target.value))}
                  disabled={submitting} className="input-field text-xs"/>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-slate-500 mb-1">최대 LOD</label>
                <input type="number" min={0} max={22} value={maxLod} onChange={e => setMaxLod(Number(e.target.value))}
                  disabled={submitting} className="input-field text-xs"/>
              </div>
            </div>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={quantize} onChange={e => setQuantize(e.target.checked)} disabled={submitting}/>
                Mesh Quantization
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={recursive} onChange={e => setRecursive(e.target.checked)} disabled={submitting}/>
                재귀 탐색
              </label>
            </div>
          </div>

          {submitting && (
            <p className="text-[11px] text-slate-400">
              원본을 순서대로 업로드하고 있습니다. 완료 후 서버에서 순차 변환·병합이 시작되며, 완료 시 이메일로 알려드립니다.
            </p>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl flex justify-end gap-2 shrink-0">
          <button onClick={handleClose} disabled={isBusy} className="btn-secondary disabled:opacity-50">취소</button>
          <button onClick={handleSubmit} disabled={!valid || isBusy}
            className="px-5 py-2 rounded-md text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            {submitting ? '처리 중...' : zippingFolder ? '폴더 압축 중...' : '변환 및 병합 시작'}
          </button>
        </div>
      </div>
    </div>
  )
}
